import { Fragment } from 'react'

export interface MatrixPlayer {
  id: number
  name: string
}

export interface MatrixCell {
  points: number
  rounds: number
  won: number
}

interface MatrixProps {
  players: MatrixPlayer[]
  /** Punkter parret har fået sammen som makkere. null/udeladt = aldrig makkere. */
  cellFor: (aId: number, bId: number) => MatrixCell | null
  ariaLabel: string
  emptyMark?: string
}

function fmt(n: number): string {
  return (n > 0 ? '+' : '') + n
}

// Fulde klasse-strenge (ikke sammensat af variabler), så Tailwind kan finde dem ved scanning.
const MINT_STEPS = ['bg-mint/10', 'bg-mint/18', 'bg-mint/30', 'bg-mint/45']
const RED_STEPS = ['bg-red/10', 'bg-red/18', 'bg-red/30', 'bg-red/45']

function stepIndex(abs: number): number {
  if (abs >= 80) return 3
  if (abs >= 40) return 2
  if (abs >= 15) return 1
  return 0
}

function cellBgClass(points: number): string {
  if (points === 0) return 'bg-text-2/10'
  const steps = points > 0 ? MINT_STEPS : RED_STEPS
  return steps[stepIndex(Math.abs(points))]
}

/** Kvadratisk matrix af point pr. makkerpar. Farve viser fortegn og størrelse. */
function Matrix({ players, cellFor, ariaLabel, emptyMark = '·' }: MatrixProps) {
  return (
    <div
      className="grid gap-1 overflow-x-auto text-center text-[0.7rem]"
      style={{ gridTemplateColumns: `52px repeat(${players.length}, minmax(44px, 1fr))` }}
      role="img"
      aria-label={ariaLabel}
    >
      <span />
      {players.map((p) => (
        <span key={p.id} className="truncate px-0.5 py-1 text-text-2">
          {p.name}
        </span>
      ))}
      {players.map((row) => (
        <Fragment key={row.id}>
          <span className="truncate py-1 text-left text-text-2">{row.name}</span>
          {players.map((col) => {
            if (row.id === col.id) {
              return (
                <span key={`${row.id}-${col.id}`} className="rounded-md bg-ink/20 py-2 font-mono text-transparent">
                  {emptyMark}
                </span>
              )
            }
            const cell = cellFor(row.id, col.id)
            if (!cell) {
              return (
                <span key={`${row.id}-${col.id}`} className="rounded-md bg-text-2/5 py-2 font-mono text-text-2/40">
                  {emptyMark}
                </span>
              )
            }
            return (
              <span
                key={`${row.id}-${col.id}`}
                title={`${row.name} + ${col.name}: vundet ${cell.won} af ${cell.rounds} runder sammen`}
                className={`grid rounded-md py-1.5 font-mono font-semibold leading-tight ${cellBgClass(cell.points)}`}
              >
                {fmt(cell.points)}
                <span className="text-[0.7em] font-normal opacity-80">{cell.won}/{cell.rounds}</span>
              </span>
            )
          })}
        </Fragment>
      ))}
    </div>
  )
}

export default Matrix
